import { useQuery } from '@tanstack/react-query'
import { apiDownload, apiGet, apiGetPage, saveBlob } from '../../modules/core/index.js'
import type { FacilityMonthRollup, InsightsMarker, InsightsPortfolio, InsightsQuarter } from './types.js'

/** The operator app's reads of the Executive Insights add-on, every one through the inspected fetch. */

const PAGE = 200

/** The twelve months (or `months`) ending with this one, as `from` and `to`. */
export function spanEnding(now: Date, months: number): { from: string; to: string } {
  const to = now.toISOString().slice(0, 7)
  const [year, month] = to.split('-').map(Number)
  const from = new Date(Date.UTC(year ?? 1970, (month ?? 1) - months, 1)).toISOString().slice(0, 7)
  return { from, to }
}

export const portfolioKey = (months: number) => ['insights', 'portfolio', months] as const
export const monthsKey = (from: string, to: string, facilities: readonly string[] | null, fields: readonly string[] | null) => ['insights', 'months', from, to, facilities === null ? 'all' : [...facilities].sort().join(','), fields === null ? '*' : fields.join(',')] as const
export const markersKey = (from: string, to: string) => ['insights', 'markers', from, to] as const

export function useInsightsPortfolio(months: number, enabled = true) {
  return useQuery({
    queryKey: portfolioKey(months),
    queryFn: () => apiGet<InsightsPortfolio>(`/insights/portfolio?months=${months}`),
    enabled,
  })
}

function monthsPath(from: string, to: string, facilities: readonly string[] | null, fields: readonly string[] | null): string {
  const chosen = facilities === null ? '' : `&facilities=${encodeURIComponent(facilities.join(','))}`
  const picked = fields === null ? '' : `&fields=${encodeURIComponent(fields.join(','))}`
  return `/insights/months?from=${from}&to=${to}${chosen}${picked}&limit=${PAGE}`
}

/** Every rollup of the span, page after page, as one list; `fields` narrows each to the tallies a screen draws, so a big client's span stays light. */
export function useInsightsMonths(from: string, to: string, facilities: readonly string[] | null, fields: readonly string[] | null, enabled = true) {
  return useQuery({
    queryKey: monthsKey(from, to, facilities, fields),
    queryFn: async () => {
      const rollups: FacilityMonthRollup[] = []
      let cursor: string | null = null
      do {
        const page: { items: FacilityMonthRollup[]; nextCursor: string | null } = await apiGetPage<FacilityMonthRollup>(monthsPath(from, to, facilities, fields), cursor)
        rollups.push(...page.items)
        cursor = page.nextCursor
      } while (cursor !== null)
      return rollups
    },
    enabled: enabled && (facilities === null || facilities.length > 0),
  })
}

export function useInsightsMarkers(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: markersKey(from, to),
    queryFn: () => apiGet<InsightsMarker[]>(`/insights/markers?from=${from}&to=${to}`),
    enabled,
  })
}

export function useInsightsQuarter(enabled = true) {
  return useQuery({
    queryKey: ['insights', 'quarter'],
    queryFn: () => apiGet<InsightsQuarter>('/insights/quarter'),
    enabled,
  })
}

/** Saves the span's months as the spreadsheet the API writes, under the name it gives. */
export async function downloadMonthsCsv(from: string, to: string): Promise<void> {
  const download = await apiDownload(`/insights/months?from=${from}&to=${to}&format=csv`)
  saveBlob(download.blob, download.fileName ?? `facility-months-${from}-to-${to}.csv`)
}
